import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { putAndValidate } from "~/shared/services/api/fetch-client.ts";
import { extractApiError } from "~/shared/utils/extract-api-error.ts";

import type { UserPreferences } from "../../schemas/user-preferences-schema.ts";
import { userPreferencesSchema } from "../../schemas/user-preferences-schema.ts";
import { INTERNAL_USER_PREFERENCES_QUERY_KEY } from "./use-get-internal-user-preferences.ts";

/**
 * Variables accepted by the internal user-preferences mutation.
 */
export type PutInternalUserPreferencesVariables = {
	/**
	 * Gets or sets the preferences payload to persist.
	 */
	preferences: UserPreferences;

	/**
	 * Gets or sets a value indicating whether the request should use the fetch
	 * `keepalive` flag so it survives page teardown (unload/pagehide saves).
	 */
	keepalive?: boolean;
};

/**
 * Persists internal (employee) users' grid preferences to the internal
 * user-preferences endpoint. Unlike the external/vendor `usePutUserPreferences`
 * hook, this one always issues the request — internal preferences ARE persisted.
 */
export const usePutInternalUserPreferences = () => {
	const { showError } = useNotificationActions();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			preferences,
			keepalive,
		}: PutInternalUserPreferencesVariables) => {
			const result = await putAndValidate(
				apiRoutes.INTERNALUSERPREFERENCES,
				preferences,
				userPreferencesSchema,
				keepalive ? { keepalive: true } : undefined,
			);
			// `putAndValidate` reports HTTP and schema-validation failures as
			// `{ success: false }` rather than throwing. Convert those to a thrown
			// error so the mutation transitions to `onError` — otherwise a failed
			// save would silently look successful and the optimistic cache would
			// drift from what was actually persisted.
			if (!result.success) {
				throw new Error(result.error || "Failed to save grid preferences.");
			}
			return result.data;
		},
		onError: async (error) => {
			await queryClient.invalidateQueries({
				queryKey: INTERNAL_USER_PREFERENCES_QUERY_KEY,
			});
			const apiError = await extractApiError(error);
			const message =
				apiError?.error ??
				(error instanceof Error && error.message ? error.message : null) ??
				"Failed to save grid preferences.";
			showError(message);
		},
	});
};
